// Free Canvas path, fully client-side (PRD §6.4, D-4).
//
// The free design needs no AI and no server compositing, so we build it in the
// browser and write it straight to Storage + content_pieces. RLS already lets a
// user insert/update their own content_pieces and upload to generated/<uid>/…
// (see migrations 20260629000002 / 20260629000004). No credits, no job row.

import { supabase } from "@/lib/supabase";
import type { Profile } from "@/lib/database.types";
import { composeCanvasDesign } from "./canvasCompositor";

const DESIGN_W = 1024;
const DESIGN_H = 1365; // ~3:4

export interface ClientCanvasInput {
  userId: string;
  profile: Profile;
  language: "ar" | "en";
  topic: string;
  caption: string;
  headline: string;
  toggles: { doctorImage: boolean; phone: boolean; logo: boolean };
  /** Reuse an existing piece (regenerate) instead of creating a new one. */
  existingPieceId?: string;
  /** AI-generated background to composite over (paid path); gradient if absent. */
  backgroundUrl?: string | null;
  /**
   * "canvas" (free path) draws the full brand layer (headline, doctor, logo,
   * phone). "ai" overlays only logo + phone — the AI image already contains the
   * doctor and headline.
   */
  mode?: "canvas" | "ai";
}

export async function runClientCanvas(
  input: ClientCanvasInput,
): Promise<{ pieceId: string; url: string }> {
  const { userId, profile, toggles } = input;
  const mode = input.mode ?? "canvas";
  const isAi = mode === "ai";

  // Doctor image (Canvas path only) — always the uploaded photo. The AI image
  // already contains the doctor, so the AI path draws none here.
  let doctorPhotoUrl: string | null = null;
  if (!isAi && toggles.doctorImage) {
    const { data } = await supabase
      .from("doctor_photos")
      .select("image_url")
      .eq("user_id", userId)
      .limit(1)
      .maybeSingle();
    doctorPhotoUrl = data?.image_url ?? null;
  }

  const palette = profile.brand_style_notes?.palette ?? [];
  const blob = await composeCanvasDesign({
    width: DESIGN_W,
    height: DESIGN_H,
    headline: input.headline,
    language: input.language,
    brandColorA: palette[0] ?? "#0F766E",
    brandColorB: palette[1] ?? "#0B3B36",
    backgroundUrl: input.backgroundUrl ?? null,
    logoUrl: toggles.logo ? profile.logo_url : null,
    phone: toggles.phone ? profile.phone : null,
    doctorPhotoUrl,
    drawHeadline: !isAi,
  });

  // Ensure a content piece exists (locked after first success — PRD §6.7).
  let pieceId = input.existingPieceId ?? "";
  if (!pieceId) {
    const { data, error } = await supabase
      .from("content_pieces")
      .insert({
        user_id: userId,
        type: "main",
        topic: input.topic,
        language: input.language,
        caption: input.caption,
        headline: input.headline,
        settings: { toggles, path: mode },
        status: "generating",
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    pieceId = data!.id as string;
  }

  // Upload to the public `generated` bucket under the owner's folder.
  const path = `${userId}/${pieceId}-${Date.now()}.png`;
  const { error: upErr } = await supabase.storage
    .from("generated")
    .upload(path, blob, { contentType: "image/png", upsert: true });
  if (upErr) throw new Error(upErr.message);
  const { data: pub } = supabase.storage.from("generated").getPublicUrl(path);

  // Save + lock the piece.
  const { error: updErr } = await supabase
    .from("content_pieces")
    .update({
      image_url: pub.publicUrl,
      visual_description: input.headline,
      status: "done",
      locked: true,
    })
    .eq("id", pieceId);
  if (updErr) throw new Error(updErr.message);

  return { pieceId, url: pub.publicUrl };
}
